<template>
  <div class="designer rules" dir="rtl">
    <header class="fl-hero">
      <div>
        <span class="vn-kicker"><b>RULES</b> کتابخانه‌ی اعتبارسنجی</span>
        <h1>قوانین آماده‌ی پاسخ</h1>
        <p>قانونی که اینجا می‌سازید در همه‌ی فرم‌ها قابل انتخاب است؛ ویرایش آن روی همه‌ی پرسش‌هایی که از آن استفاده می‌کنند اثر می‌گذارد.</p>
      </div>
      <button type="button" class="fl-primary" @click="edit(null)"><VnIcon name="plus" />قانون تازه</button>
      <span class="vn-bars fl-bars" aria-hidden="true" />
    </header>

    <div class="rl-layout">
      <section class="rl-list">
        <label class="fl-search"><VnIcon name="search" /><input v-model="query" type="search" placeholder="جست‌وجوی قانون" /></label>
        <div v-if="error" class="ins-error" role="alert">{{ error }}</div>
        <p v-if="loading" class="ins-note">در حال بارگذاری…</p>
        <button v-for="rule in shown" :key="rule.id" type="button" class="rl-row" :class="{ active: draft && draft.id === rule.id }" @click="edit(rule)">
          <span class="rl-name">{{ rule.name }}</span>
          <span class="rl-sum">{{ describeRule(rule).join(' · ') || 'بدون محدودیت' }}</span>
          <span class="rl-uses vn-num" :class="{ zero: !rule.uses }">{{ rule.uses ? `${fa(rule.uses)} کاربرد` : 'بدون کاربرد' }}</span>
        </button>
        <p v-if="!loading && !shown.length" class="ins-note">قانونی پیدا نشد.</p>
      </section>

      <aside v-if="draft" class="rl-editor" aria-label="ویرایش قانون">
        <header><b>{{ draft.id ? 'ویرایش قانون' : 'قانون تازه' }}</b><small v-if="draft.uses">در {{ fa(draft.uses) }} پرسش استفاده شده است</small></header>
        <div class="ins-pane">
          <label class="ins-field"><span>نام قانون</span><input ref="name" v-model="draft.name" maxlength="30" placeholder="مثلاً: کد ملی (اجباری)" /></label>
          <label class="ins-toggle"><input v-model="draft.is_mandatory" type="checkbox" /><span><b>پاسخ اجباری</b></span></label>
          <fieldset class="ins-box"><legend>عدد و مبلغ</legend>
            <div class="ins-grid">
              <label class="ins-field"><span>کمترین</span><input v-model.number="draft.min_value" type="number" dir="ltr" /></label>
              <label class="ins-field"><span>بیشترین</span><input v-model.number="draft.max_value" type="number" dir="ltr" /></label>
              <label class="ins-field wide"><span>پیام خطا</span><input v-model="draft.value_error_msg_fa" /></label>
            </div>
          </fieldset>
          <fieldset class="ins-box"><legend>طول متن</legend>
            <div class="ins-grid">
              <label class="ins-field"><span>کمترین نویسه</span><input v-model.number="draft.min_lenght" type="number" min="0" dir="ltr" /></label>
              <label class="ins-field"><span>بیشترین نویسه</span><input v-model.number="draft.max_lenght" type="number" min="0" dir="ltr" /></label>
              <label class="ins-field wide"><span>پیام خطا</span><input v-model="draft.lenght_error_msg_fa" /></label>
            </div>
          </fieldset>
          <fieldset class="ins-box"><legend>الگو</legend>
            <div class="ins-chips"><button v-for="item in regexPresets" :key="item.label" type="button" :class="{ active: draft.regex === item.regex }" @click="useRegex(item)">{{ item.label }}</button></div>
            <label class="ins-field"><span>عبارت باقاعده</span><input v-model="draft.regex" dir="ltr" class="vn-mono" :aria-invalid="regexError ? 'true' : 'false'" /></label>
            <small v-if="regexError" class="ins-error">{{ regexError }}</small>
            <label class="ins-field"><span>پیام خطا</span><input v-model="draft.regex_error_msg_fa" /></label>
          </fieldset>
          <fieldset class="ins-box"><legend>تعداد انتخاب</legend>
            <div class="ins-grid">
              <label class="ins-field"><span>کمترین</span><input v-model.number="draft.min_choice_count" type="number" min="0" dir="ltr" /></label>
              <label class="ins-field"><span>بیشترین</span><input v-model.number="draft.max_choice_count" type="number" min="0" dir="ltr" /></label>
              <label class="ins-field wide"><span>پیام خطا</span><input v-model="draft.choice_count_error_msg_fa" /></label>
            </div>
          </fieldset>
          <p v-if="formError" class="ins-error" role="alert">{{ formError }}</p>
        </div>
        <footer>
          <button v-if="draft.id && !draft.uses" type="button" class="ins-link danger" :disabled="busy" @click="remove">حذف قانون</button>
          <span class="wz-gap" />
          <button type="button" class="ins-btn" :disabled="busy" @click="draft = null">انصراف</button>
          <button type="button" class="fl-primary small" :disabled="busy || !draft.name.trim() || !!regexError" @click="save">{{ busy ? 'در حال ذخیره…' : 'ذخیره' }}</button>
        </footer>
      </aside>
      <aside v-else class="rl-editor empty"><EmptyState kind="search" size="sm" title="یک قانون را انتخاب کنید" description="یا قانون تازه‌ای بسازید." /></aside>
    </div>
  </div>
</template>

<script>
import VnIcon from '@/components/VnIcon/index.vue';
import EmptyState from '@/components/EmptyState/index.vue';
import { REGEX_PRESETS, RULE_FIELDS, describeRule, emptyRuleFields, fa } from './designerModel';
import './designer.css';

export default {
  name: 'RulesLibrary',
  components: { VnIcon, EmptyState },
  data: () => ({ rules: [], loading: true, error: '', query: '', draft: null, busy: false, formError: '', regexPresets: REGEX_PRESETS }),
  computed: {
    project() { return this.$STORE.state.userConfig.setProjectId; },
    shown() { const q = this.query.trim(); return this.rules.filter(rule => !q || rule.name.includes(q) || (rule.regex || '').includes(q)); },
    regexError() {
      if (!this.draft || !this.draft.regex) return '';
      try { new RegExp(this.draft.regex); return ''; } catch (_) { return 'این الگو معتبر نیست.'; }
    },
  },
  mounted() { this.load(); },
  methods: {
    fa, describeRule,
    async load() {
      this.loading = true;
      const res = await this.$ApiServiceLayer.get(`/core/api/admin/designer/catalog/?p=${this.project}`);
      this.loading = false;
      if (res.status !== 200) { this.error = this.$ApiServiceLayer.getErrorMessage(res); return; }
      this.rules = res.data.validations;
    },
    edit(rule) {
      this.formError = '';
      this.draft = rule ? { ...emptyRuleFields(), ...rule } : { ...emptyRuleFields(), id: null, name: '', uses: 0 };
      this.$nextTick(() => this.$refs.name && this.$refs.name.focus());
    },
    useRegex(item) { this.draft.regex = item.regex; if (!this.draft.regex_error_msg_fa) this.draft.regex_error_msg_fa = item.message; },
    payload() {
      const body = { name: this.draft.name.trim() };
      RULE_FIELDS.forEach((name) => { const value = this.draft[name]; body[name] = value === '' ? null : value; });
      return body;
    },
    async save() {
      if (this.draft.uses && !window.confirm(`این قانون در ${fa(this.draft.uses)} پرسش استفاده شده و تغییر آن روی همه‌ی آن‌ها اثر دارد. ذخیره شود؟`)) return;
      this.busy = true; this.formError = '';
      const url = `/core/api/admin/designer/rules/${this.draft.id ? this.draft.id + '/' : ''}?p=${this.project}`;
      const res = this.draft.id ? await this.$ApiServiceLayer.put(url, '', this.payload()) : await this.$ApiServiceLayer.post(url, '', this.payload());
      this.busy = false;
      if (res.status === 200 || res.status === 201) {
        this.$notify({ group: 'tc', type: 'success', text: 'قانون ذخیره شد.' });
        const id = res.data.id;
        await this.load();
        this.edit(this.rules.find(rule => rule.id === id) || null);
        return;
      }
      this.formError = (res.data && res.data.detail) || this.$ApiServiceLayer.getErrorMessage(res);
    },
    async remove() {
      if (!window.confirm(`قانون «${this.draft.name}» حذف شود؟`)) return;
      this.busy = true;
      const res = await this.$ApiServiceLayer.delete(`/core/api/admin/designer/rules/${this.draft.id}/?p=${this.project}`);
      this.busy = false;
      if (res.status === 204) { this.draft = null; this.load(); return; }
      this.formError = (res.data && res.data.detail) || this.$ApiServiceLayer.getErrorMessage(res);
    },
  },
};
</script>

<style scoped>
.rules { display: grid; gap: 16px; }
.fl-hero { position: relative; display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 16px; padding: 22px 22px 25px; border-radius: var(--r-md); background: var(--navy-900); color: #fff; overflow: hidden; }
.fl-hero .vn-kicker { color: var(--shell-text-2); }
.fl-hero h1 { margin: 6px 0 4px; font-size: 20px; font-weight: 900; color: #fff; }
.fl-hero p { margin: 0; max-width: 620px; font-size: 12.5px; line-height: 1.9; color: var(--shell-text-2); }
.fl-bars { position: absolute; inset-inline: 0; bottom: 0; direction: ltr; }
.fl-primary { display: inline-flex; align-items: center; gap: 8px; min-height: 42px; padding: 0 16px; border: 2px solid var(--teal-500); border-radius: var(--r-xs); background: var(--teal-500); color: var(--navy-900); font: inherit; font-size: 13px; font-weight: 900; cursor: pointer; box-shadow: 3px 3px 0 var(--pink-500); --icon-accent: var(--navy-900); }
.fl-primary.small { min-height: 38px; box-shadow: 3px 3px 0 var(--navy-900); }
.fl-primary:disabled { opacity: .45; box-shadow: none; cursor: default; }
.rl-layout { display: grid; grid-template-columns: minmax(0, 1fr) 420px; gap: 16px; align-items: start; }
.rl-list { display: grid; gap: 6px; }
#app .fl-search { display: flex; align-items: center; gap: 8px; min-height: 40px; margin: 0 0 6px; padding: 0 12px; border: 1px solid var(--line-strong); border-radius: var(--r-xs); background: var(--surface); color: var(--text-3); }
#app .fl-search input { flex: 1; min-width: 0; height: 38px; border: 0; background: transparent; box-shadow: none; padding: 0; font: inherit; font-size: 13px; }
#app .fl-search input:focus { outline: none; }
.rl-row { display: grid; grid-template-columns: minmax(140px, 220px) 1fr auto; align-items: center; gap: 12px; padding: 10px 14px; border: 1px solid var(--line); border-radius: var(--r-sm); background: var(--surface); text-align: right; font: inherit; cursor: pointer; }
.rl-row:hover { border-color: var(--line-strong); }
.rl-row.active { border-color: var(--navy-900); box-shadow: 4px 4px 0 var(--teal-500); }
.rl-name { font-size: 13px; font-weight: 900; color: var(--text); }
.rl-sum { font-size: 11.5px; color: var(--text-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; direction: rtl; unicode-bidi: plaintext; }
.rl-uses { font-size: 11px; font-weight: 800; color: var(--teal-700); }
.rl-uses.zero { color: var(--text-4); }
.rl-editor { position: sticky; top: 76px; display: flex; flex-direction: column; max-height: calc(100vh - 100px); border: 1px solid var(--line); border-radius: var(--r-md); background: var(--surface); overflow: hidden; }
.rl-editor.empty { padding: 24px; }
.rl-editor > header { display: grid; padding: 12px 14px; background: var(--navy-900); color: #fff; }
.rl-editor > header small { color: var(--shell-text-2); font-size: 11px; }
.rl-editor .ins-pane { flex: 1; overflow-y: auto; padding: 14px; }
.rl-editor > footer { display: flex; align-items: center; gap: 8px; padding: 12px 14px; border-top: 1px solid var(--line); background: var(--surface-sunken); }
.wz-gap { flex: 1; }
.danger { color: var(--pink-700); }
@media (max-width: 1000px) { .rl-layout { grid-template-columns: 1fr; } .rl-editor { position: static; max-height: none; } .rl-row { grid-template-columns: 1fr auto; } .rl-sum { grid-column: 1 / -1; grid-row: 2; } }
</style>
